import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { c as create$3, d as create$7, F as FormController } from './form.js';
import { a as BusinessFormStep, B as BusinessFormStepV2 } from './business-form-types.js';
import { c as config } from './config.js';
import { A as Api } from './Api.js';
import './Pagination.js';
import './Payment.js';
import './Payout.js';
import { C as ComponentErrorCodes, a as ComponentErrorSeverity } from './ComponentError.js';
import { d as defineCustomElement$4 } from './form-control-checkbox2.js';
import { d as defineCustomElement$3 } from './form-control-error-text2.js';
import { d as defineCustomElement$2 } from './form-control-help-text2.js';
import { d as defineCustomElement$1 } from './business-terms-conditions-text.js';

const businessTermsConditionsSchema = (allowOptionalFields) => {
    const schema = create$3({
        accepted: create$7()
            .oneOf([true], 'You must agree to the terms and conditions to continue')
            .required('You must agree to the terms and conditions to continue')
    });
    const easySchema = create$3({
        accepted: create$7().nullable()
    });
    return allowOptionalFields ? easySchema : schema;
};

const BusinessTermsConditionsFormStep = /*@__PURE__*/ proxyCustomElement(class BusinessTermsConditionsFormStep extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.submitted = createEvent(this, "submitted", 7);
        this.stepCompleted = createEvent(this, "form-step-completed", 7);
        this.formLoading = createEvent(this, "formLoading", 7);
        this.errorEvent = createEvent(this, "error-event", 7);
        this.fetchData = async () => {
            this.formLoading.emit(true);
            try {
                const response = await this.api.get(this.businessEndpoint);
                this.acceptedTermsBefore = response.data.terms_conditions_accepted;
            }
            catch (error) {
                this.errorEvent.emit({
                    errorCode: ComponentErrorCodes.FETCH_ERROR,
                    message: error.message,
                    severity: ComponentErrorSeverity.ERROR,
                    data: error,
                });
            }
            finally {
                this.formLoading.emit(false);
            }
        };
        this.sendData = async (onSuccess) => {
            this.formLoading.emit(true);
            try {
                const payload = this.termsPayload;
                const response = await this.api.post(this.termsConditionsEndpoint, payload);
                this.handleResponse(response, onSuccess);
            }
            catch (error) {
                this.errorEvent.emit({
                    errorCode: ComponentErrorCodes.POST_ERROR,
                    message: error.message,
                    severity: ComponentErrorSeverity.ERROR,
                    data: error,
                });
            }
            finally {
                this.formLoading.emit(false);
            }
        };
        this.inputHandler = (name, value) => {
            this.formController.setValues(Object.assign(Object.assign({}, this.formController.values.getValue()), { [name]: value }));
        };
        this.formController = undefined;
        this.errors = {};
        this.acceptedTermsBefore = undefined;
        this.authToken = undefined;
        this.businessId = undefined;
        this.allowOptionalFields = undefined;
    }
    get businessEndpoint() {
        return `entities/business/${this.businessId}`;
    }
    get termsConditionsEndpoint() {
        return 'entities/terms_and_conditions';
    }
    get formHelperText() {
        return this.acceptedTermsBefore ? 'You have already accepted the terms and conditions.' : null;
    }
    get termsPayload() {
        return {
            business_id: this.businessId,
            accepted: this.formController.values.getValue().accepted,
            user_agent: window.navigator.userAgent
        };
    }
    async componentWillLoad() {
        this.api = Api({ authToken: this.authToken, apiOrigin: config.proxyApiOrigin });
        this.formController = new FormController(businessTermsConditionsSchema(this.allowOptionalFields));
        if (this.businessId && this.authToken) {
            this.fetchData();
        }
    }
    handleResponse(response, onSuccess) {
        if (response.error) {
            this.errorEvent.emit({
                errorCode: ComponentErrorCodes.POST_ERROR,
                message: response.error.message,
                severity: ComponentErrorSeverity.ERROR,
                data: response.error,
            });
        }
        else {
            onSuccess();
        }
        this.submitted.emit({ data: response, metadata: { completedStep: BusinessFormStep.termsAndConditions } });
        this.stepCompleted.emit({ data: response, formStep: BusinessFormStepV2.termsAndConditions });
    }
    async validateAndSubmit({ onSuccess }) {
        if (this.acceptedTermsBefore) {
            this.submitted.emit({ metadata: { completedStep: BusinessFormStep.termsAndConditions } });
            onSuccess();
        }
        else {
            this.formController.validateAndSubmit(() => this.sendData(onSuccess));
        }
    }
    ;
    componentDidLoad() {
        this.formController.errors.subscribe(errors => {
            this.errors = Object.assign({}, errors);
        });
    }
    render() {
        return (h("form", { key: '3f269771c368a23afd1c7b139b0fce416d18f161' }, h("fieldset", { key: 'a94a5bf100cb5a10b156b8cf94dc85f03cc1f6ae' }, h("legend", { key: 'facb4a29cebd5147fe3c35d336df04546b550d74' }, "Terms and Conditions"), h("hr", { key: '3dafaab7d2d35bf9a00f87da643f6e1efaa7b147' }), h("justifi-business-terms-conditions-text", { key: '60a1370c8b0d817d79130e8607f6d663c4648c78' }), h("br", { key: 'e72e81a34b31a00ebe3a0d01e24dc096df7bc2a9' }), h("div", { key: '676ed858195c1b90a9e4fdc22ba9654ff944dc93', class: "row-gy-3" }, h("div", { key: '20039587eb6144a05078799f6a41feb59f43dbd2', class: "col-12" }, h("form-control-checkbox", { key: 'fc8eaa81e530b55f24e2ec994b97b4c181af94b2', name: "accepted", label: "I agree to the terms and conditions", inputHandler: this.inputHandler, errorText: this.errors.accepted, disabled: this.acceptedTermsBefore, helpText: this.formHelperText }))))));
    }
}, [0, "justifi-business-terms-conditions-form-step", {
        "authToken": [1, "auth-token"],
        "businessId": [1, "business-id"],
        "allowOptionalFields": [4, "allow-optional-fields"],
        "formController": [32],
        "errors": [32],
        "acceptedTermsBefore": [32],
        "validateAndSubmit": [64]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["justifi-business-terms-conditions-form-step", "form-control-checkbox", "form-control-error-text", "form-control-help-text", "justifi-business-terms-conditions-text"];
    components.forEach(tagName => { switch (tagName) {
        case "justifi-business-terms-conditions-form-step":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, BusinessTermsConditionsFormStep);
            }
            break;
        case "form-control-checkbox":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "form-control-error-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "form-control-help-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "justifi-business-terms-conditions-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { BusinessTermsConditionsFormStep as B, defineCustomElement as d };
